/**
 * Flash Sale API Requests
 *
 * Hooks for fetching flash sale data from GraphQL API.
 * Business logic is 100% identical to mobile module-flash-sale.
 *
 * @module @kidslabs/module-flash-sale/api/requests
 */

import { useCallback } from 'react';
import {
    type Banner,
    type FlashSaleProduct,
    type FlashSaleProductsV2,
    type FlashSaleProductsWithPaginationQuery,
    type FlashSaleProductsWithPaginationQueryVariables,
    useFlashSaleProductListQuery,
    useFlashSaleProductsWithPaginationLazyQuery,
    useFlashSaleProductsWithPaginationQuery,
    useFlashSaleSessionsQuery,
} from '../../types/front.graphql';

/**
 * Hook for lazy fetching flash sale products with pagination
 *
 * This hook provides:
 * - Lazy loading of flash sale products
 * - Pagination support with fetchMore
 * - Banner items and layout
 *
 * Business logic is 100% identical to mobile:
 * - currentPage and totalPages calculation
 * - isLoadMore flag for pagination control
 * - updateQuery for appending items on load more
 */
export function useFlashSaleProductsLazy() {
    const [fetchFlashSaleProducts, { data, loading, error, fetchMore }] = useFlashSaleProductsWithPaginationLazyQuery();

    const currentPage = data?.flashSaleProductsV3?.flash_sale_items?.page_info?.current_page ?? 1;
    const totalPages = data?.flashSaleProductsV3?.flash_sale_items?.page_info?.total_pages ?? 1;

    const isLoadMore = currentPage < totalPages;

    const getFlashSaleProducts = useCallback(
        (variables: FlashSaleProductsWithPaginationQueryVariables) => {
            if (variables.currentPage > 1) {
                return fetchMore({
                    variables,
                    updateQuery: (
                        prev: FlashSaleProductsWithPaginationQuery,
                        { fetchMoreResult }: { fetchMoreResult: FlashSaleProductsWithPaginationQuery }
                    ) => {
                        if (!fetchMoreResult.flashSaleProductsV3) return prev;

                        return {
                            ...fetchMoreResult,
                            flashSaleProductsV3: {
                                ...fetchMoreResult.flashSaleProductsV3,
                                flash_sale_items: {
                                    ...fetchMoreResult.flashSaleProductsV3.flash_sale_items,
                                    items: [
                                        ...(prev.flashSaleProductsV3?.flash_sale_items?.items ?? []),
                                        ...(fetchMoreResult.flashSaleProductsV3.flash_sale_items?.items ?? []),
                                    ],
                                },
                            },
                        };
                    },
                });
            }

            return fetchFlashSaleProducts({
                fetchPolicy: 'network-only',
                variables,
            });
        },
        [fetchFlashSaleProducts, fetchMore]
    );

    return {
        getFlashSaleProducts,
        banner: data?.flashSaleProductsV3?.banner_items?.items?.filter(
            (item: Banner | null): item is Banner => item?.image !== null
        ),
        layoutBanner: data?.flashSaleProductsV3?.banner_items?.layout,
        flashSaleProduct: data?.flashSaleProductsV3?.flash_sale_items?.items as FlashSaleProduct[],
        currentPage,
        totalPages,
        isLoadMore,
        totalCount: data?.flashSaleProductsV3?.flash_sale_items?.total_count,
        loading,
        error,
    };
}

/**
 * Hook for fetching flash sale products with initialization option
 *
 * @param initialized - If true, uses cache-first policy; otherwise network-only
 */
export function useFlashSaleProducts(initialized = false) {
    const { data, loading, error, refetch } = useFlashSaleProductsWithPaginationQuery({
        fetchPolicy: initialized ? 'cache-first' : 'network-only',
        variables: {
            pageSize: 20,
            currentPage: 1,
            flashsaleId: 0,
        },
    });

    return {
        flashSaleProduct: data?.flashSaleProductsV3?.flash_sale_items?.items,
        flashSaleData: data?.flashSaleProductsV3 as FlashSaleProductsV2,
        loading,
        error,
        refetch,
    };
}

/**
 * Hook for fetching flash sale sessions
 *
 * Returns all available flash sale sessions with their timing and status.
 * Uses network-only to always get fresh session data.
 */
export const useFlashSaleSessions = () => {
    const { data, loading, error, refetch } = useFlashSaleSessionsQuery({
        fetchPolicy: 'network-only',
    });

    return {
        data: data?.flashSaleSessions,
        loading,
        error,
        refetch,
    };
};

/**
 * Hook for fetching flash sale product by SKU
 *
 * Used to get flash sale data for a specific product.
 *
 * @param sku - Product SKU
 * @param skip - Whether to skip the query
 */
export const useFlashSaleProductsList = (sku: string, skip?: boolean) => {
    const { data, loading, error, refetch } = useFlashSaleProductListQuery({
        fetchPolicy: 'network-only',
        variables: { sku },
        skip,
    });

    const salesData = data?.flashSaleProduct?.sales_data;

    return {
        salesData,
        loading,
        error,
        refetch,
    };
};
